import React, { useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import toLower from 'lodash/toLower';
import compact from 'lodash/compact';
import trim from 'lodash/trim';

import {
  ProjectsTasksSelectDeepTreeRoot,
  ProjectsTasksSelectDeepTreeNode,
  ProjectsTasksTreeSelectOption
} from './useProjectsTasksSelectDeepTree.types';

const LVL_INDENT = 15;

function filterTreeItem(
  node: ProjectsTasksSelectDeepTreeRoot | ProjectsTasksSelectDeepTreeNode,
  input: string
): ProjectsTasksSelectDeepTreeRoot | ProjectsTasksSelectDeepTreeNode | null {
  if (toLower(node.name).includes(input)) {
    return node;
  }

  const childTasks =
    'childProjectsTasks' in node ? node.childProjectsTasks : undefined;

  if (!isEmpty(childTasks)) {
    const mappedChildren = compact(
      map(childTasks, (child) => filterTreeItem(child, input))
    );
    if (!isEmpty(mappedChildren)) {
      return {
        ...node,
        childProjectsTasks: mappedChildren
      } as ProjectsTasksSelectDeepTreeRoot | ProjectsTasksSelectDeepTreeNode;
    }
  }

  return null;
}

function transformTreeToData({
  node,
  cb,
  valueType,
  lvl = 0
}: {
  node: ProjectsTasksSelectDeepTreeRoot | ProjectsTasksSelectDeepTreeNode;
  cb: (dataItem: ProjectsTasksTreeSelectOption) => void;
  valueType: 'uuid' | 'nanoId' | 'originalId';
  lvl?: number;
}) {
  const value =
    valueType === 'uuid'
      ? node.uuid
      : valueType === 'nanoId'
      ? node.nanoId
      : node.originalId;

  cb({
    value,
    label: <span style={{ paddingLeft: LVL_INDENT * lvl }}>{node.name}</span>,
    type: node.type
  });

  const childTasks =
    'childProjectsTasks' in node ? node.childProjectsTasks : undefined;

  if (!isEmpty(childTasks)) {
    forEach(childTasks, (child) => {
      transformTreeToData({
        node: child,
        cb,
        valueType,
        lvl: lvl + 1
      });
    });
  }
}

function flattenTree(
  node: ProjectsTasksSelectDeepTreeRoot | ProjectsTasksSelectDeepTreeNode,
  valueType: 'uuid' | 'nanoId' | 'originalId'
): ProjectsTasksTreeSelectOption[] {
  const result: ProjectsTasksTreeSelectOption[] = [];

  function traverse(
    n: ProjectsTasksSelectDeepTreeRoot | ProjectsTasksSelectDeepTreeNode
  ) {
    const value =
      valueType === 'uuid'
        ? n.uuid
        : valueType === 'nanoId'
        ? n.nanoId
        : n.originalId;

    result.push({
      value,
      label: n.name,
      type: n.type
    });

    const childTasks =
      'childProjectsTasks' in n ? n.childProjectsTasks : undefined;
    if (!isEmpty(childTasks)) {
      forEach(childTasks, traverse);
    }
  }

  traverse(node);
  return result;
}

interface ProjectsTasksSelectDeepTreeOptions {
  projectsTask: ProjectsTasksSelectDeepTreeRoot | null;
  valueType?: 'uuid' | 'nanoId' | 'originalId';
}

function useProjectsTasksSelectDeepTree({
  projectsTask,
  valueType = 'uuid'
}: ProjectsTasksSelectDeepTreeOptions) {
  const [input, setInput] = useState<string>('');

  const filteredTree = useMemo(() => {
    if (!projectsTask) {
      return null;
    }
    if (!input) {
      return projectsTask;
    }
    const simpleInput = toLower(trim(input));
    return filterTreeItem(projectsTask, simpleInput);
  }, [projectsTask, input]);

  const data = useMemo<ProjectsTasksTreeSelectOption[]>(() => {
    if (!filteredTree) {
      return [];
    }
    const dataItems: ProjectsTasksTreeSelectOption[] = [];
    const childTasks =
      'childProjectsTasks' in filteredTree
        ? filteredTree.childProjectsTasks
        : undefined;

    if (childTasks) {
      forEach(childTasks, (child) => {
        transformTreeToData({
          node: child,
          cb: (dataItem) => dataItems.push(dataItem),
          valueType,
          lvl: 0
        });
      });
    }
    return dataItems;
  }, [filteredTree, valueType]);

  const plainData = useMemo<ProjectsTasksTreeSelectOption[]>(() => {
    if (!projectsTask) {
      return [];
    }
    const result: ProjectsTasksTreeSelectOption[] = [];
    const childTasks =
      'childProjectsTasks' in projectsTask
        ? projectsTask.childProjectsTasks
        : undefined;

    if (childTasks) {
      forEach(childTasks, (child) => {
        result.push(...flattenTree(child, valueType));
      });
    }
    return result;
  }, [projectsTask, valueType]);

  return {
    data,
    plainData,
    onInputChange: setInput
  };
}

export default useProjectsTasksSelectDeepTree;
